/**
 * 프론트엔드 전용 열거형(Enum) 정의 모듈입니다.
 *
 * 주요 열거형:
 * - MainTabs: 메인 탭 종류 (실전/시뮬/백테스트/전략)
 * - StrategyTabs/StrategyButtons: 전략 패널 탭 및 버튼
 * - MarketTabs: 시장 정보 탭 (포지션/진입/후보/투자/종목)
 * - 그리드 컬럼 ID들 (SymbolGridColIds, PositionGridColIds 등)
 * - SearchOperatorType: 검색 연산자 종류
 * - EnterQueueItemStatus: 진입 대기열 항목 상태
 *
 * @remarks
 * UI 컴포넌트에서 탭, 버튼, 그리드 컬럼 등을 식별하는 데 사용됩니다.
 */
import type { UserTriggerType } from '@bit-box/common';
import { InvestMode, enumValues, TriggerType } from '@bit-box/common';

export const MainTabs = {
  ...InvestMode,
  CREATOR: 'creator',
  STRATEGY: 'strategy',
} as const;
export type MainTabType = InvestMode | 'creator' | 'strategy';

// export const isInvestModeTab = (tabKey: MainTabType): tabKey is InvestMode => {
export const isInvestModeTab = (tabKey: MainTabType): tabKey is InvestMode => {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  return enumValues(InvestMode).includes(tabKey as InvestMode);
};

// 우측 전략 패널의 상단 탭들
export enum StrategyTabs {
  TRIGGER = 'trigger',
  SIGNAL = 'signal',
  INDICATOR = 'indicator',
}

// 맨 우측 세로 메뉴의 전략 버튼들
export enum StrategyButtons {
  SIGNAL_ENTER = TriggerType.SIGNAL_ENTER,
  SIGNAL = StrategyTabs.SIGNAL,
  INDICATOR = StrategyTabs.INDICATOR,
  SIGNAL_EXIT = TriggerType.SIGNAL_EXIT,
  TRAILING_EXIT = TriggerType.TRAILING_EXIT,
  EXCLUSIVE_EXIT = TriggerType.EXCLUSIVE_EXIT,
  OVERLAPPING_EXIT = TriggerType.OVERLAPPING_EXIT,
}

export function isTriggerButton(button: string): button is UserTriggerType {
  return [
    StrategyButtons.SIGNAL_ENTER,
    StrategyButtons.SIGNAL_EXIT,
    StrategyButtons.TRAILING_EXIT,
    StrategyButtons.EXCLUSIVE_EXIT,
    StrategyButtons.OVERLAPPING_EXIT,
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  ].includes(button as StrategyButtons);
}

export enum MarketTabs {
  POSITION = 'position',
  // ENTRY = 'entry',
  INVEST = 'invest',
  SYMBOL = 'symbol',
}

export enum SymbolGridColIds {
  SELECTED = 'selected',
  SHORT_SYMBOL = 'shortSymbol',
  LEVERAGE = 'leverage',
  PRICE = 'price',
  QUOTE_VOLUME = 'quoteVolume',
  PERCENTAGE = 'percentage',
  // 고점 대비 하락률
  FROM_HIGH = 'fromHigh',
  // 저점 대비 상승률
  FROM_LOW = 'fromLow',
  // 등락폭
  HIGH_LOW_CHANGE = 'highLowChange',
  // 진입 횟수
  // ENTER_COUNT = 'enterCount',
  CORRELATION = 'correlation', //상관계수
}

export enum SignalGridColIds {
  MATCHED_AT = 'matchedAt',
  // 신호 매칭 후 경과 시간
  ELAPSED_TIME = 'elapsedTime',
  SHORT_SYMBOL = 'shortSymbol',
  PRICE = 'price',
  QUOTE_VOLUME = 'quoteVolume',
  PERCENTAGE = 'percentage',
  // 고점 대비 하락률
  FROM_HIGH = 'fromHigh',
  // 저점 대비 상승률
  FROM_LOW = 'fromLow',
  // 등락폭
  HIGH_LOW_CHANGE = 'highLowChange',
  CHANGE = 'change',
  // [진입] 컬럼인데 키가 matchInfos 인 이유
  //    -> 컬럼 키가 gridData에 존재하면 해당 속성이 달라질 때마다 자동으로 셀을 갱신해줌.
  //    -> 매칭 상태가 달라질 때마다 그에 따라 진입 버튼들의 색상이 실시간 갱신되도록 하기 위함.
  ENTER_POSITION = 'matchInfos',
}

export enum PositionGridColIds {
  TIMESTAMP = 'timestamp',
  // 진입 후 경과 시간
  ELAPSED_TIME = 'elapsedTime',
  SHORT_SYMBOL = 'shortSymbol',
  QUOTE_AMOUNT = 'quoteAmount',
  ENTER_PRICE = 'enterPrice',
  PRICE = 'price',
  NET_PNL = 'netPnl',
  NET_ROE = 'netRoe',
  EXIT_TRIGGER = 'exitTrigger',
  TREND = 'trend',
  LIQUIDATION_PRICE = 'liquidationPrice',
  EXIT_POSITION = 'exitPosition',
}

export enum OrderGridColIds {
  NO = 'no',
  SYMBOL = 'symbol',
  QUOTE_AMOUNT = 'quoteAmount',
  DATETIME = 'datetime',
  POSITION_ORDER_TYPE = 'positionOrderType',
  LEVERAGE = 'leverage',
  MARGIN_MODE = 'marginMode',
  ENTER_COST = 'enter_cost',
  PRICE = 'price',
  AMOUNT = 'amount',
  EXIT_COST = 'exit_cost',
  PNL = 'pnl',
  TRADE_FEE = 'tradeFee',
  NET_PNL = 'netPnl',
}

export enum SearchOperatorType {
  NONE = '',
  EQUAL_OR_GREATER_THAN = '>=',
  EQUAL_OR_LESS_THAN = '<=',
  GREATER_THAN = '>',
  LESS_THAN = '<',
  INCLUDE = 'include',
  EQUAL = '=',
}

export enum DropAction {
  COPY = 'copy',
  MOVE = 'move',
  SHARE = 'share',
}

export enum TriggerTarget {
  SESSION = 'session',
  POSITION = 'position',
}

export enum MenuGroups {
  Strategy = 'strategy',
  Market = 'market',
  CheckList = 'checkList',
}

/** 진입 대기열 항목 상태 */
export enum EnterQueueItemStatus {
  /** 진입 대기 */
  PENDING = 'pending',
  /** 진입 중 */
  IN_PROGRESS = 'in_progress',
  /** 진입 완료 */
  COMPLETED = 'completed',
  /** 경고 - 사용자 선택 대기 */
  WARNING_WAIT = 'warning_wait',
  /** 건너뜀 (진입 불가 또는 아니오 선택) */
  SKIPPED = 'skipped',
  /** 사용자 취소 */
  CANCELLED = 'cancelled',
  /** 오류 발생 */
  ERROR = 'error',
}

/** 경고 유형 */
export enum EnterWarningType {
  /** 중복 진입 */
  DUPLICATE_POSITION = 'duplicate_position',
  /** 청산 신호 발생 중 */
  EXIT_SIGNAL = 'exit_signal',
}

/** 진입 불가 사유 */
export enum EnterBlockedReason {
  /** 반대 포지션 보유 중 */
  OPPOSITE_POSITION = 'opposite_position',
  /** 포지션 없음 (반대 포지션 진입 시) */
  NO_POSITION = 'no_position',
}
